import { api } from './client'

export type Permission = 'MANAGE_PIPELINE' | 'SEND_BULK'

export interface User {
  id: string
  email: string
  fullName: string
  role: string
  status: string
  permissions: string[]
  /** Agent e-mail identity: signature (plain text / simple HTML) and avatar URL. */
  emailSignature: string | null
  photoUrl: string | null
  /** Work phone, shown on the owner card. */
  phone: string | null
  /** Assigned Twilio number (E.164) inbound SMS to it routes to this agent. */
  smsNumber: string | null
  /** Invited by email and hasn't set a password yet. */
  invitePending?: boolean
}

/** Roster available to any authenticated user (unlike the admin-only /api/users): name + work contact. */
export interface Member {
  id: string
  fullName: string
  role: string
  email: string | null
  phone: string | null
  status: string
  photoUrl: string | null
}

export interface CreateUserInput {
  email: string
  fullName: string
  password: string
  role: string
  permissions?: string[]
  emailSignature?: string | null
  photoUrl?: string | null
  phone?: string | null
  smsNumber?: string | null
}

/**
 * Admin edit. email/status/permissions/emailSignature/photoUrl omitted = unchanged;
 * permissions [] clears all grants; empty string clears signature/photo.
 * A supplied email must be valid and unique within the agency.
 */
export interface UpdateUserInput {
  fullName: string
  role: string
  email?: string | null
  status?: string | null
  permissions?: string[] | null
  emailSignature?: string | null
  photoUrl?: string | null
  phone?: string | null
  smsNumber?: string | null
}

export async function listUsers(): Promise<User[]> {
  return (await api.get<User[]>('/api/users')).data
}

export async function listMembers(): Promise<Member[]> {
  return (await api.get<Member[]>('/api/users/members')).data
}

export async function createUser(input: CreateUserInput): Promise<User> {
  return (await api.post<User>('/api/users', input)).data
}

/** Send a fresh invite link to someone who hasn't joined yet. */
export async function resendInvite(id: string): Promise<User> {
  return (await api.post<User>(`/api/users/${id}/invite`)).data
}

export async function updateUser(id: string, input: UpdateUserInput): Promise<User> {
  return (await api.put<User>(`/api/users/${id}`, input)).data
}

/** Upload/replace a user's avatar (multipart). Returns the user with its new photoUrl. */
export async function uploadUserPhoto(id: string, file: File): Promise<User> {
  const form = new FormData()
  form.append('file', file)
  // Null the default application/json so the browser sets multipart/form-data + boundary
  // (else the backend sees no multipart part and the send 500s). Same as uploadFile.
  return (await api.post<User>(`/api/users/${id}/photo`, form, {
    headers: { 'Content-Type': null },
  })).data
}

// --- the signed-in user's own profile ("My profile") ---

/** Own-profile edit: name, phone, signature, photo. null = unchanged, '' = clear. */
export interface UpdateProfileInput {
  fullName: string
  phone?: string | null
  emailSignature?: string | null
  photoUrl?: string | null
}

export async function getMe(): Promise<User> {
  return (await api.get<User>('/api/users/me')).data
}

export async function updateMe(input: UpdateProfileInput): Promise<User> {
  return (await api.put<User>('/api/users/me', input)).data
}

export async function uploadMyPhoto(file: File): Promise<User> {
  const form = new FormData()
  form.append('file', file)
  return (await api.post<User>('/api/users/me/photo', form, { headers: { 'Content-Type': null } })).data
}

export async function changeMyPassword(currentPassword: string, newPassword: string): Promise<void> {
  await api.post('/api/users/me/password', { currentPassword, newPassword })
}
